import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import { LandscapeSchedule, UpcomingLandscapeSchedule, CreateLandscapeScheduleParams } from "@/types";

export const useGetLandscapeSchedules = (landscapeId?: number) =>
  useQuery<{ schedules: LandscapeSchedule[] }>({
    queryKey: ['landscape-schedules', 'list', landscapeId],
    queryFn: () =>
      apiService.get<{ schedules: LandscapeSchedule[] }>(`/landscapes/${landscapeId}/schedules`),
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!landscapeId,
  });

export const useCreateLandscapeSchedule = () =>
  useMutation<
    { schedule: LandscapeSchedule },
    unknown,
    { landscapeId: number; payload: CreateLandscapeScheduleParams }
  >({
    mutationFn: ({ landscapeId, payload }) =>
      apiService.post<{ schedule: LandscapeSchedule }, CreateLandscapeScheduleParams>(
        `/landscapes/${landscapeId}/schedules`,
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landscape-schedules'] });
    },
  });

export const useUpdateLandscapeSchedule = () =>
  useMutation<
    { schedule: LandscapeSchedule },
    unknown,
    { id: number; payload: Partial<CreateLandscapeScheduleParams> }
  >({
    mutationFn: ({ id, payload }) =>
      apiService.patch<{ schedule: LandscapeSchedule }, Partial<CreateLandscapeScheduleParams>>(
        `/landscape-schedules/${id}`,
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landscape-schedules'] });
    },
  });

export const useDeleteLandscapeSchedule = () =>
  useMutation<void, unknown, { id: number }>({
    mutationFn: ({ id }) => apiService.delete<void>(`/landscape-schedules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landscape-schedules'] });
    },
  });

export const useRunLandscapeScheduleNow = () =>
  useMutation<
    { landscape_id: number },
    unknown,
    { id: number }
  >({
    mutationFn: ({ id }) =>
      apiService.post<{ landscape_id: number }>(`/landscape-schedules/${id}/run-now`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landscape-schedules'] });
    },
  });

export const useGetUpcomingLandscapeSchedules = (limit = 5) =>
  useQuery<{ items: UpcomingLandscapeSchedule[] }>({
    queryKey: ['landscape-schedules', 'upcoming', limit],
    queryFn: () =>
      apiService.get<{ items: UpcomingLandscapeSchedule[] }>(
        `/landscape-schedules/upcoming?limit=${limit}`
      ),
    staleTime: DEFAULT_STALE_TIME,
  });
